import http, { ApiRequestConfig } from "@services/httpService"
import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type { RequestProgressDto } from "@models/approvalProgress/approvalProgressModel"

/**
 * Duyệt/từ chối bước cuối kích hoạt side-effect nặng phía server (hoàn tất booking/contract,
 * enqueue notification) — 30s mặc định của axios từng làm client abort trong khi server vẫn commit,
 * khiến user bấm duyệt lại và gặp lỗi "đã duyệt rồi". Đồng bộ với legacy approvalService
 * (updateRequestStatus cũng dùng 120s cho cùng backend ApprovalCore.UpdateRequestStatusAsync).
 */
const APPROVE_REJECT_TIMEOUT_MS = 120000

class ApprovalInboxOutboxService {
  public async getInbox(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ApprovalInboxOutbox/GetInbox",
      { params }
    )
    return res.data.result
  }

  public async getOutbox(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ApprovalInboxOutbox/GetOutbox",
      { params }
    )
    return res.data.result
  }

  public async getProcessed(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ApprovalInboxOutbox/GetProcessed",
      { params }
    )
    return res.data.result
  }

  public async getInboxCount(): Promise<any> {
    const res = await http.get(
      "api/services/app/ApprovalInboxOutbox/GetInboxCount"
    )
    return res.data.result
  }

  /**
   * `options.silent`: caller tự báo kết quả nên KHÔNG bật popup thành công/lỗi của lượt này.
   * Dùng cho duyệt hàng loạt — mỗi phiếu một lời gọi, để mặc định thì người dùng nhận đúng N
   * popup và popup cuối che mất phiếu nào đã lỗi.
   */
  public async approve(
    body: {
      requestId: number
      comment?: string
      /** Bước duyệt màn hình ĐANG hiển thị — BE chặn lượt duyệt dựa trên danh sách đã cũ. */
      expectedCurrentStep?: number
    },
    options?: { silent?: boolean }
  ): Promise<any> {
    const res = await http.post(
      "api/services/app/ApprovalInboxOutbox/Approve",
      body,
      {
        timeout: APPROVE_REJECT_TIMEOUT_MS,
        skipErrorNotification: options?.silent,
      } as ApiRequestConfig
    )
    if (!options?.silent) {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("APPROVAL_SUCCESSFULLY")
      )
    }
    return res.data.result
  }

  public async reject(body: {
    requestId: number
    comment: string
  }): Promise<any> {
    const res = await http.post(
      "api/services/app/ApprovalInboxOutbox/Reject",
      body,
      { timeout: APPROVE_REJECT_TIMEOUT_MS }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("REJECT_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async getRequestProgress(requestId: number): Promise<RequestProgressDto> {
    const res = await http.get(
      "api/services/app/ApprovalInboxOutbox/GetRequestProgress",
      { params: { requestId } }
    )
    return res.data.result
  }
}

export default new ApprovalInboxOutboxService()
